import { modoDev, msalInstance, scopesApi } from '@/lib/auth';
import { obtenerUsuarioActual } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { LogOut, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { LogoAdelante } from './LogoAdelante';

const linkBase = 'text-sm transition-colors';
const linkInactive = 'text-zinc-400 hover:text-white';
const linkActive = 'text-white';

interface Props {
  /** Nombre de la app que aparece a la derecha del logo. Si se omite, no se muestra. */
  nombreApp?: string;
}

/**
 * Reacquiere el access token de la API forzando refresh. Útil cuando la app
 * se "cuelga" por un token expirado. En modo dev es un no-op informativo.
 */
async function recargarToken(): Promise<void> {
  if (modoDev || !msalInstance) {
    toast.info('Modo desarrollo: no hay token real que recargar.');
    return;
  }

  const cuenta = msalInstance.getAllAccounts()[0];
  if (!cuenta) {
    toast.error('Sin sesión activa.');
    return;
  }

  try {
    await msalInstance.acquireTokenSilent({
      scopes: scopesApi,
      account: cuenta,
      forceRefresh: true,
    });
    toast.success('Token recargado.');
  } catch {
    // Si el refresh silencioso falla, caemos a interacción redirect.
    await msalInstance.acquireTokenRedirect({ scopes: scopesApi, account: cuenta });
  }
}

/** Cierra la sesión de MSAL. En modo dev es un no-op informativo. */
function cerrarSesion(): void {
  if (modoDev || !msalInstance) {
    toast.info('Modo desarrollo: sesión simulada, no hay logout real.');
    return;
  }
  msalInstance.logoutRedirect();
}

export function HeaderAdelante({ nombreApp }: Props) {
  const usuario = obtenerUsuarioActual();
  const iniciales = usuario?.iniciales ?? '?';
  const nombreCompleto = usuario?.name ?? 'Sin sesión';
  const correo = usuario?.username ?? '';

  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic afuera o presionar Escape.
  useEffect(() => {
    if (!abierto) return;

    function alClicAfuera(evento: MouseEvent) {
      if (contenedorRef.current && !contenedorRef.current.contains(evento.target as Node)) {
        setAbierto(false);
      }
    }
    function alEscape(evento: KeyboardEvent) {
      if (evento.key === 'Escape') setAbierto(false);
    }

    document.addEventListener('mousedown', alClicAfuera);
    document.addEventListener('keydown', alEscape);
    return () => {
      document.removeEventListener('mousedown', alClicAfuera);
      document.removeEventListener('keydown', alEscape);
    };
  }, [abierto]);

  return (
    <header className="bg-[#0A0A0A] text-white">
      <div className="container mx-auto flex h-14 max-w-7xl items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <LogoAdelante altura={22} />
          <span className="text-sm font-bold tracking-wider">ADELANTE.</span>
          {nombreApp && (
            <>
              <span className="text-zinc-700">|</span>
              <span className="text-sm text-zinc-300">{nombreApp}</span>
            </>
          )}
        </div>

        <nav className="flex items-center gap-6">
          <NavLink
            to="/"
            end
            className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkInactive)}
          >
            Inicio
          </NavLink>
        </nav>

        <div className="relative" ref={contenedorRef}>
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-adelante-primary text-xs font-bold text-adelante-foreground outline-none ring-offset-2 ring-offset-[#0A0A0A] focus-visible:ring-2 focus-visible:ring-ring"
            aria-haspopup="menu"
            aria-expanded={abierto}
            aria-label={`Menú de usuario: ${nombreCompleto}`}
            title={nombreCompleto}
          >
            {iniciales}
          </button>

          {abierto && (
            <div
              role="menu"
              className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
            >
              <div className="border-border border-b px-4 py-3">
                <p className="font-medium text-sm leading-tight">{nombreCompleto}</p>
                {correo && <p className="mt-0.5 text-muted-foreground text-xs">{correo}</p>}
              </div>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAbierto(false);
                  void recargarToken();
                }}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <span className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Recargar token
                </span>
                <span className="text-muted-foreground text-xs">si la app se cuelga</span>
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAbierto(false);
                  cerrarSesion();
                }}
                className="flex w-full items-center gap-2 border-border border-t px-4 py-2.5 text-left text-destructive text-sm transition-colors hover:bg-accent"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
